import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CustomCheckbox } from '../../CustomControls/CustomCheckbox';
import { QuantityInput } from '../../../containers/Inventory/Shared/QuantityInput';

// Compartido entre el popup obligatorio de finalización (CU-1, mandatory=true) y el botón
// "Adicionales y Facturables" (CU-2, mandatory=false, readOnly según permiso Coordinación.Edit).
export const AdditionalProceduresModal = ({
    show,
    mandatory,
    readOnly,
    items,
    saving,
    onConfirm,
    onClose,
}) => {
    const { t } = useTranslation();
    const [checkedIds, setCheckedIds] = useState(() => new Set(items.filter(i => i.checked).map(i => i.id)));
    // Desacoplado de checkedIds: desmarcar y volver a marcar un ítem en la misma apertura del modal
    // no resetea su cantidad. Se inicializa para todos los ítems (no solo los marcados) para que
    // nunca falte una entrada mientras dure esta instancia del modal.
    const [quantities, setQuantities] = useState(() => {
        const initial = {};
        items.forEach(i => { initial[i.id] = i.quantity > 0 ? i.quantity : 1; });
        return initial;
    });

    const handleToggle = (id, checked) => {
        setCheckedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    };

    const handleQuantityChange = (id, value) => {
        // QuantityInput solo valida el tope máximo (resolveQuantityInput); el mínimo de RN-14
        // hay que garantizarlo acá, y normalizar a number ya que resolveQuantityInput devuelve string.
        const numeric = Math.max(1, Number(value) || 1);
        setQuantities(prev => ({ ...prev, [id]: numeric }));
    };

    const handleConfirm = () => {
        onConfirm(items.map(i => ({
            id: i.id,
            checked: checkedIds.has(i.id),
            quantity: quantities[i.id] ?? 1,
        })));
    };

    const hasSelection = checkedIds.size > 0;

    return (
        <Modal
            show={show}
            onHide={mandatory ? undefined : onClose}
            backdrop={mandatory ? "static" : true}
            keyboard={!mandatory}
        >
            <Modal.Header closeButton={!mandatory}>
                <Modal.Title>{t("protocolNotifications.additionalProcedures_title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted">{t("protocolNotifications.additionalProcedures_description")}</p>
                {items.map(item => (
                    <div
                        key={item.id}
                        style={{ display: 'grid', gridTemplateColumns: '1fr 80px', alignItems: 'center', columnGap: 12, marginBottom: 10 }}
                    >
                        <CustomCheckbox
                            name={`additional-procedure-${item.id}`}
                            isChecked={checkedIds.has(item.id)}
                            disabled={readOnly || saving}
                            label={item.description}
                            onChange={(_name, checked) => handleToggle(item.id, checked)}
                        />
                        <QuantityInput
                            id={`quantity-${item.id}`}
                            name={`quantity-${item.id}`}
                            style={{ width: 80 }}
                            min={1}
                            allowEmpty={false}
                            value={quantities[item.id] ?? 1}
                            disabled={readOnly || saving || !checkedIds.has(item.id)}
                            onChange={value => handleQuantityChange(item.id, value)}
                        />
                    </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
                {readOnly ? (
                    <Button bsStyle="default" onClick={onClose} disabled={saving}>
                        {t("buttons.close")}
                    </Button>
                ) : (
                    <>
                        <Button bsStyle="default" onClick={handleConfirm} disabled={hasSelection || saving}>
                            {t("protocolNotifications.additionalProcedures_none")}
                        </Button>
                        <Button bsStyle="success" onClick={handleConfirm} disabled={!hasSelection || saving}>
                            {t("buttons.save")}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

AdditionalProceduresModal.propTypes = {
    show: PropTypes.bool.isRequired,
    mandatory: PropTypes.bool,
    readOnly: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        checked: PropTypes.bool,
        quantity: PropTypes.number,
    })).isRequired,
    saving: PropTypes.bool,
    onConfirm: PropTypes.func.isRequired,
    onClose: PropTypes.func,
};

AdditionalProceduresModal.defaultProps = {
    mandatory: false,
    readOnly: false,
    saving: false,
};
